import type { SettingsTab } from '@/lib/constants';
import type { IntegrationType } from '@/lib/integrations/settings-pages';
import type { Route } from 'next';

import { AUTH_QUERY_PARAMS } from '@/lib/constants';
import { settingsTabForIntegration } from '@/lib/integrations/settings-pages';

type PathSearchParamValue = string | number | boolean | null | undefined;

export type InternalHref<T extends string = string> = Route<T> | URL;
export const CANONICAL_INBOX_PATH = '/inbox' as const;

/** Appends the set search params to pathname; null, undefined and empty values are left out. */
export function buildPathWithSearchParams(
  pathname: string,
  searchParams?: Record<string, PathSearchParamValue>,
): Route {
  if (!searchParams) {
    return pathname as Route;
  }

  const params = new URLSearchParams();

  Object.entries(searchParams).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }

    params.set(key, String(value));
  });

  return `${pathname}${params.size > 0 ? `?${params.toString()}` : ''}` as Route;
}

export function buildPrPath({
  owner,
  repo,
  number,
  pullNumber,
  params,
  searchParams,
}: {
  owner: string;
  repo: string;
  number?: number | string;
  pullNumber?: number | string;
  params?: Record<string, PathSearchParamValue>;
  searchParams?: Record<string, PathSearchParamValue>;
}): Route {
  const resolvedNumber = number ?? pullNumber;

  if (resolvedNumber === undefined) {
    throw new Error('buildPrPath requires either number or pullNumber');
  }

  const pathname = `/pr/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/pull/${encodeURIComponent(String(resolvedNumber))}`;
  return buildPathWithSearchParams(pathname, params ?? searchParams);
}

/** A codebase scan with one of its findings open. */
export function buildCodebaseScanPath({
  repoScanId,
  violationId,
}: {
  repoScanId: string;
  violationId: string;
}): Route {
  return buildPathWithSearchParams(`/codebase-scan/${encodeURIComponent(repoScanId)}`, {
    violationId,
  });
}

export function buildPrReviewUrl({
  owner,
  repo,
  number,
  pullNumber,
  utmSource,
  utmMedium,
  baseUrl,
}: {
  owner: string;
  repo: string;
  number?: number | string;
  pullNumber?: number | string;
  utmSource: string;
  utmMedium?: string | null;
  baseUrl?: string | null;
}): string {
  const path = buildPrPath({
    owner,
    repo,
    number,
    pullNumber,
    params: {
      utm_source: utmSource,
      utm_medium: utmMedium,
    },
  });

  if (!baseUrl) {
    return path;
  }

  return `${baseUrl.replace(/\/$/, '')}${path}`;
}

export function buildSettingsPath(options?: {
  tab?: SettingsTab | null;
  integration?: IntegrationType | null;
  hash?: string | null;
  params?: Record<string, PathSearchParamValue>;
}): Route {
  const tab = options?.integration ? settingsTabForIntegration(options.integration) : options?.tab;
  const path = buildPathWithSearchParams('/settings', {
    tab,
    integration: options?.integration,
    ...options?.params,
  });
  return (options?.hash ? `${path}#${options.hash}` : path) as Route;
}

export function buildSignInPath({ returnTo }: { returnTo?: string | null } = {}): Route {
  return buildPathWithSearchParams('/sign-in', {
    [AUTH_QUERY_PARAMS.RETURN_TO]: returnTo,
  });
}
